import { useEffect, useRef, useState } from "react";
import DOMPurify from "dompurify";
import { Button } from "react-bootstrap";
import { MegaphoneFill, Paperclip } from "react-bootstrap-icons";

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const calculateNoticePosition = (
  noticeIndex,
  modalWidth,
  viewportWidth,
  viewportHeight,
) => {
  const estimatedHeight = Math.min(Math.max(viewportHeight * 0.42, 280), 360);
  const gap = 20;
  const columns = Math.max(1, Math.floor((viewportWidth - 24) / (modalWidth + gap)));
  const column = noticeIndex % columns;
  const row = Math.floor(noticeIndex / columns);
  const totalWidth = columns * modalWidth + (columns - 1) * gap;
  const leftStart = (viewportWidth - totalWidth) / 2;
  const orderHistoryHeader =
    typeof document !== "undefined"
      ? document.querySelector(".order-history-container .section-title")
      : null;
  const topStart = orderHistoryHeader
    ? orderHistoryHeader.getBoundingClientRect().top
    : Math.max(12, (viewportHeight - estimatedHeight) / 2);
  const left = leftStart + column * (modalWidth + gap);
  const top = topStart + row * (estimatedHeight + gap);

  return {
    left: clamp(left, 12, Math.max(12, viewportWidth - modalWidth - 12)),
    top: clamp(top, 12, Math.max(12, viewportHeight - estimatedHeight - 12)),
  };
};

const formatDate = (value) => {
  if (!value) return "-";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return new Intl.DateTimeFormat("ko-KR", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
};

const renderAttachment = (attachmentFile) => {
  if (!attachmentFile) return null;

  const text = String(attachmentFile);
  const isLink = /^(https?:\/\/|\/)/i.test(text);

  if (isLink) {
    return (
      <a href={text} target="_blank" rel="noreferrer" className="text-decoration-none">
        <Paperclip className="me-1" />
        {text}
      </a>
    );
  }

  return (
    <span>
      <Paperclip className="me-1" />
      {text}
    </span>
  );
};

const OutboundNoticeModal = ({
  show,
  notice,
  noticeIndex = 0,
  onHide,
  onHideToday,
}) => {
  const modalWidth = typeof window !== "undefined" ? Math.min(460, Math.max(window.innerWidth - 24, 320)) : 460;
  const viewportWidth = typeof window !== "undefined" ? window.innerWidth : 1200;
  const viewportHeight = typeof window !== "undefined" ? window.innerHeight : 800;
  const initialPosition = calculateNoticePosition(
    noticeIndex,
    modalWidth,
    viewportWidth,
    viewportHeight,
  );

  const [position, setPosition] = useState(() => initialPosition);
  const [isDragging, setIsDragging] = useState(false);
  const dragStateRef = useRef(null);

  useEffect(() => {
    if (show) {
      setPosition(
        calculateNoticePosition(
          noticeIndex,
          modalWidth,
          viewportWidth,
          viewportHeight,
        ),
      );
    }
  }, [show, noticeIndex, modalWidth, viewportHeight, viewportWidth]);

  useEffect(() => {
    if (!isDragging) {
      return;
    }

    const handleMouseMove = (event) => {
      const dragState = dragStateRef.current;
      if (!dragState) {
        return;
      }

      const nextLeft = clamp(
        dragState.startLeft + (event.clientX - dragState.startX),
        8,
        Math.max(8, window.innerWidth - modalWidth - 8),
      );
      const nextTop = clamp(
        dragState.startTop + (event.clientY - dragState.startY),
        8,
        Math.max(8, window.innerHeight - 120),
      );

      setPosition({ left: nextLeft, top: nextTop });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      dragStateRef.current = null;
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging, modalWidth]);

  const handleDragStart = (event) => {
    if (event.button !== 0) {
      return;
    }

    if (event.target.closest("button, a, input, textarea, select, label")) {
      return;
    }

    event.preventDefault();
    dragStateRef.current = {
      startX: event.clientX,
      startY: event.clientY,
      startLeft: position.left,
      startTop: position.top,
    };
    setIsDragging(true);
  };

  const handleHeaderDoubleClick = () => {
    setPosition(
      calculateNoticePosition(
        noticeIndex,
        modalWidth,
        viewportWidth,
        viewportHeight,
      ),
    );
  };

  if (!notice) {
    return null;
  }

  // Prepare sanitized HTML content. If content is HTML-escaped (contains &lt;), unescape first.
  let rawContent = String(notice.content || "");
  try {
    if (/&lt;|&gt;/.test(rawContent)) {
      const parser = typeof window !== "undefined" ? new DOMParser() : null;
      if (parser) {
        const doc = parser.parseFromString(rawContent, "text/html");
        rawContent = doc.body ? doc.body.innerHTML : rawContent;
      }
    }
  } catch (e) {
    // fallback: leave rawContent as-is
  }

  const sanitizedContent = DOMPurify.sanitize(rawContent, { ADD_ATTR: ["class", "style"] });

  return (
    <div
      className="outbound-notice-window"
      style={{
        display: show ? "block" : "none",
        zIndex: 2000 + noticeIndex * 10,
        left: `${position.left}px`,
        top: `${position.top}px`,
        width: `${modalWidth}px`,
      }}
    >
      <div
        className="outbound-notice-window-header"
        onMouseDown={handleDragStart}
        onDoubleClick={handleHeaderDoubleClick}
      >
        <div className="d-flex align-items-center gap-2 fw-bold mb-0">
          <span className="outbound-notice-icon">
            <MegaphoneFill />
          </span>
          <span className="outbound-notice-title">공지사항</span>
        </div>
        <button type="button" className="outbound-notice-close" onClick={onHide} aria-label="닫기">
          ×
        </button>
      </div>

      <div className="outbound-notice-window-body">
        <section className="outbound-notice-item">
          <div className="outbound-notice-item-head">
            <h5 className="outbound-notice-item-title">{notice.title}</h5>
            <small className="outbound-notice-item-date">
              게시기간 {formatDate(notice.publish_start_date)} - {formatDate(notice.publish_end_date)}
            </small>
          </div>

                  <div
                    className="outbound-notice-item-body"
                    dangerouslySetInnerHTML={{ __html: sanitizedContent }}
                  />

          {notice.attachment_file && (
            <div className="outbound-notice-item-footer">
              <strong className="me-2">첨부파일</strong>
              {renderAttachment(notice.attachment_file)}
              {(() => {
                const text = String(notice.attachment_file);
                const isImage = /\.(png|jpe?g|gif|webp|svg)(\?.*)?$/i.test(text) || text.startsWith("data:image/") || /\/images\//i.test(text);
                if (isImage) {
                  const src = /^(https?:)?\/\//.test(text) ? text : text.startsWith("/") ? text : text;
                  return (
                    <div className="outbound-notice-item-image mt-2">
                      <img src={src} alt="attachment" style={{ maxWidth: "100%", borderRadius: 8 }} />
                    </div>
                  );
                }
                return null;
              })()}
            </div>
          )}
        </section>
      </div>

      <div className="outbound-notice-window-footer">
        <div className="d-flex flex-wrap gap-2 justify-content-end w-100">
          <Button variant="outline-secondary" onClick={onHideToday}>
            오늘하루 안보이기
          </Button>
          <Button variant="dark" onClick={onHide}>
            닫기
          </Button>
        </div>
      </div>

      <style>{`
        .outbound-notice-window {
          position: fixed;
          margin: 0;
          left: 12px;
          top: 12px;
          border: 1px solid #f1c6b0;
          border-radius: 18px;
          overflow: hidden;
          display: flex;
          flex-direction: column;
          max-height: calc(100vh - 24px);
          box-shadow: 0 20px 60px rgba(15, 23, 42, 0.18);
          background: #fff;
        }

        .outbound-notice-window-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          background: linear-gradient(180deg, #e35c20 0%, #df5518 100%);
          color: #fff;
          border-bottom: 0;
          padding: 14px 18px;
          cursor: move;
          user-select: none;
          flex: 0 0 auto;
        }

        .outbound-notice-close {
          appearance: none;
          border: 0;
          background: transparent;
          color: #fff;
          font-size: 30px;
          line-height: 1;
          padding: 0 2px;
          cursor: pointer;
        }

        .outbound-notice-window-body {
          padding: 12px 14px 10px;
          background: #fff;
          flex: 1 1 auto;
          min-height: 0;
          overflow-y: auto;
        }

        .outbound-notice-window-footer {
          border-top: 0;
          padding: 0 18px 18px;
          justify-content: center;
          background: #fff;
          flex: 0 0 auto;
        }

        .outbound-notice-item {
          border: 1px solid #ece7e2;
          border-radius: 16px;
          padding: 14px 16px;
          background: #fffdfc;
          box-shadow: 0 6px 18px rgba(15, 23, 42, 0.05);
        }

        .outbound-notice-item-head {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 12px;
          margin-bottom: 12px;
          flex-wrap: wrap;
        }

        .outbound-notice-item-title {
          margin: 0;
          font-size: 1rem;
          font-weight: 700;
          color: #111827;
          flex: 1 1 220px;
          min-width: 0;
        }

        .outbound-notice-item-date {
          color: #6b7280;
          white-space: normal;
          font-size: 0.8rem;
          text-align: right;
        }

        .outbound-notice-item-body {
          white-space: pre-wrap;
          line-height: 1.65;
          font-size: 0.92rem;
          color: #1f2937;
          text-align: center;
          padding: 0 4px;
        }

        .outbound-notice-item-body img {
          max-width: 100%;
          height: auto;
          display: block;
          margin: 12px auto;
          border-radius: 8px;
        }

        /* Quill alignment classes (ensure stored content alignment shows correctly) */
        .ql-align-center { text-align: center; }
        .ql-align-right { text-align: right; }
        .ql-align-justify { text-align: justify; }
        .ql-align-left { text-align: left; }

        /* also support inline style text-align preserved by sanitizer */
        .outbound-notice-item-body [style*="text-align:center"] { text-align: center; }
        .outbound-notice-item-body [style*="text-align:right"] { text-align: right; }
        .outbound-notice-item-body [style*="text-align:justify"] { text-align: justify; }
        .outbound-notice-item-body [style*="text-align:left"] { text-align: left; }

        .outbound-notice-item-footer {
          margin-top: 14px;
          padding-top: 12px;
          border-top: 1px solid #eee7e1;
          color: #6b7280;
        }

        @media (max-width: 576px) {
          .outbound-notice-window {
            left: 6px;
            top: 6px;
            width: calc(100vw - 12px) !important;
            max-height: calc(100vh - 12px);
          }

          .outbound-notice-item {
            padding: 12px 12px 14px;
          }

          .outbound-notice-item-head {
            flex-direction: column;
            gap: 6px;
          }

          .outbound-notice-item-date {
            text-align: left;
          }
        }
      `}</style>
    </div>
  );
};

export default OutboundNoticeModal;